import ReactSelect, { components } from 'react-select'
import Icon from '../Icon'
import classNames from 'classnames'
import CircleLoader from '../CircleLoader'

import './option.scss'
import './select.scss'

const Option = props => {
    const { data, isSelected, isFocused, label } = props
    const { icon, secondaryText } = data

    return (
        <components.Option {...props}>
            <div
                className={classNames('option', {
                    'is-selected': isSelected,
                    'is-focused': isFocused,
                })}>
                {icon && (
                    <div className="option__icon">
                        <Icon type={icon} size="xs">
                            {icon}
                        </Icon>
                    </div>
                )}
                <div
                    className={classNames('option__text', {
                        'option__text-wrapper': !!secondaryText,
                    })}>
                    <p
                        className={classNames({
                            'option__main-text': !!secondaryText,
                        })}>
                        {label}
                    </p>
                    {secondaryText && (
                        <div className="option__secondary-text">
                            {secondaryText.map((text, index) => (
                                <p key={index}>{text}</p>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </components.Option>
    )
}

const DropdownIndicator = ({ selectProps, ...props }) => {
    if (!selectProps.arrow) return null

    return <components.DropdownIndicator {...props} />
}

const Control = ({ children, selectProps, ...props }) => {
    const { icon, size } = selectProps

    if (icon) {
        const getIconSize = () => {
            if (size === 'small') return 'xs'
            if (size === 'large') return 'md'

            return 'sm'
        }

        return (
            <components.Control {...props}>
                <div className="select__icon">
                    <Icon type={icon} size={getIconSize()} />
                </div>
                {children}
            </components.Control>
        )
    }

    return <components.Control {...props}>{children}</components.Control>
}

const LoadingIndicator = () => (
    <div className="select__loading-indicator">
        <CircleLoader size="small" />
    </div>
)

export default function Select({
    components: propComponents,
    size,
    scaleEffect,
    borders = true,
    icon,
    noOptionsCustomMessage,
    enterTermMessage,
    arrow = true,
    isTabletOrDesktop,
    ...reactSelectProps
}) {
    const props = {
        ...reactSelectProps,
        className: 'select',
        classNamePrefix: 'select',
        icon,
        arrow,
        size,
        noOptionsMessage: ({ inputValue }) => {
            if (enterTermMessage && inputValue === '') return enterTermMessage

            return noOptionsCustomMessage ? noOptionsCustomMessage : <T>select.no-results</T>
        },
        styles: {
            valueContainer: provided => ({
                ...provided,
                fontSize: '16px',
                padding: !!icon && !isTabletOrDesktop ? '2px 6px' : '2px 16px',
            }),
            control: provided => {
                const borderColor = !borders ? 'transparent' : provided.borderColor
                const hoverBorderColor = !borders ? 'transparent' : provided['&:hover'].borderColor

                const getControlHeight = () => {
                    if (size === 'small') return '44px'
                    if (size === 'large') return '70px'
                    return '50px'
                }
                return {
                    ...provided,
                    boxShadow: 'none',
                    fontFamily: 'IndigoSans',
                    height: reactSelectProps.isMulti ? false : getControlHeight(),
                    minHeight: getControlHeight(),
                    borderColor,
                    '&:hover': { borderColor: hoverBorderColor },
                }
            },
            option: provided => ({ ...provided, padding: 0 }),
            container: (provided, state) => {
                const getScaleEffectStyles = () => {
                    if (scaleEffect && state.isFocused)
                        return {
                            transform: 'scale(1.1) translateY(-20px)',
                        }

                    if (scaleEffect && !state.isFocused) {
                        return { transform: 'scale(1)' }
                    }
                }
                return {
                    ...provided,
                    transition: 'transform .2s ease-in-out',
                    ...getScaleEffectStyles(),
                }
            },
            input: provided => ({
                ...provided,
                fontFamily: 'inherit',
            }),
            placeholder: provided => ({
                ...provided,
                color: size === 'large' ? '#552373' : provided.color,
            }),
        },
        components: {
            Option,
            IndicatorSeparator: null,
            DropdownIndicator,
            Control,
            LoadingIndicator,
            ...propComponents,
        },
        theme: theme => ({
            ...theme,
            colors: {
                ...theme.colors,
                primary: '#b80e80',
                neutral30: '#000',
            },
            spacing: {
                ...theme.spacing,
            },
        }),
    }

    return <ReactSelect {...props} />
}
